import {
  regions as getRegions,
  provinces as getProvinces,
  cities as getCities,
  barangays as getBarangays,
} from 'select-philippines-address';

// Normalize a PH place name for fuzzy matching against the PSGC dataset:
// strips "City of"/"Municipality of"/"Province of", the trailing "City"/
// "Municipality" words, punctuation and accents, then collapses whitespace.
const norm = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/province of/g, ' ')
    .replace(/city of/g, ' ')
    .replace(/municipality of/g, ' ')
    .replace(/\bcity\b/g, ' ')
    .replace(/\bmunicipality\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const bestMatch = (list, field, target) => {
  const t = norm(target);
  if (!t || !list?.length) return null;
  // 1) exact normalized match
  let m = list.find((x) => norm(x[field]) === t);
  if (m) return m;
  // 2) prefix either direction (handles "Cabuyao" vs "Cabuyao City")
  m = list.find((x) => {
    const n = norm(x[field]);
    return n && (n.startsWith(t) || t.startsWith(n));
  });
  if (m) return m;
  // 3) substring either direction
  m = list.find((x) => {
    const n = norm(x[field]);
    return n && (n.includes(t) || t.includes(n));
  });
  return m || null;
};

// Cache the flattened province list (heaviest scan — all regions) across calls.
let _allProvincesCache = null;
async function getAllProvinces() {
  if (_allProvincesCache) return _allProvincesCache;
  const regs = await getRegions();
  const all = [];
  for (const r of regs) {
    // eslint-disable-next-line no-await-in-loop
    all.push(...(await getProvinces(r.region_code)));
  }
  _allProvincesCache = all;
  return all;
}

/**
 * Resolve loosely-geocoded PH address parts into the CANONICAL names used by
 * the `select-philippines-address` (PSGC) dataset, so they line up exactly with
 * the cascading Province → City → Barangay dropdowns.
 * Returns { province, city, barangay } — each an exact dataset name or '' when
 * no confident match is found.
 */
export async function resolvePHAddress({ province, city, barangay } = {}) {
  const out = { province: '', city: '', barangay: '' };
  try {
    const allProv = await getAllProvinces();
    const prov = bestMatch(allProv, 'province_name', province);
    if (!prov) return out;
    out.province = prov.province_name;

    const cs = await getCities(prov.province_code);
    const c = bestMatch(cs, 'city_name', city);
    if (!c) return out;
    out.city = c.city_name;

    const bs = await getBarangays(c.city_code);
    const b = bestMatch(bs, 'brgy_name', barangay);
    if (b) out.barangay = b.brgy_name;
  } catch (e) {
    /* return whatever resolved so far */
  }
  return out;
}

export default resolvePHAddress;
